/**
 * ============================================================================
 * FILE GALLERY COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Gallery view for images and files
 */

import React, { useState } from 'react';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Modal from '../common/Modal';
import EmptyState from '../common/EmptyState';

/**
 * File Gallery Component
 * @param {Object} props
 * @param {Array} props.files - Array of file objects
 * @param {Function} props.onFileClick - File click callback
 * @param {number} props.columns - Number of columns (default: 4)
 * @returns {JSX.Element}
 */
const FileGallery = ({ files = [], onFileClick, columns = 4 }) => {
  const { formatDate, language } = useI18n();
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  const isImage = (file) => {
    const type = file.type || file.mimeType || '';
    return type.startsWith('image/');
  };

  const getFileIcon = (file) => {
    const type = file.type || file.mimeType || '';
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.includes('pdf')) return '📄';
    return '📎';
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    if (onFileClick) {
      onFileClick(file);
    }
  };

  const imageFiles = files.filter((f) => isImage(f));
  const otherFiles = files.filter((f) => !isImage(f));

  if (files.length === 0) {
    return (
      <Card variant="glass" className="p-12">
        <EmptyState
          icon="🖼️"
          title={language === 'fr' ? 'Aucun fichier' : 'No files'}
          description={
            language === 'fr'
              ? 'Aucun fichier à afficher'
              : 'No files to display'
          }
        />
      </Card>
    );
  }

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">🖼️</span>
            {language === 'fr' ? 'Galerie de fichiers' : 'File Gallery'}
          </h3>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'gradient' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              ⊞
            </Button>
            <Button
              variant={viewMode === 'list' ? 'gradient' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              ☰
            </Button>
          </div>
        </div>

        {/* Images Grid */}
        {imageFiles.length > 0 && (
          <div>
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
              {language === 'fr' ? 'Images' : 'Images'} ({imageFiles.length})
            </h4>
            {viewMode === 'grid' ? (
              <div
                className={`grid gap-4 ${
                  columns === 2
                    ? 'grid-cols-2'
                    : columns === 3
                    ? 'grid-cols-3'
                    : columns === 4
                    ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
                    : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
                }`}
              >
                {imageFiles.map((file, index) => (
                  <Card
                    key={file.id || file.url || index}
                    variant="outlined"
                    hover
                    clickable
                    onClick={() => handleFileClick(file)}
                    className="relative group animate-fadeInUp"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="aspect-square overflow-hidden rounded-xl">
                      <img
                        src={file.url || file.path}
                        alt={file.name || file.fileName || `Image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-xl flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-2xl">
                        👁️
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {imageFiles.map((file, index) => (
                  <Card
                    key={file.id || file.url || index}
                    variant="outlined"
                    hover
                    clickable
                    onClick={() => handleFileClick(file)}
                    className="flex items-center gap-4 p-3 animate-fadeInUp"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <img
                      src={file.url || file.path}
                      alt={file.name || file.fileName}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h5 className="font-black text-gray-900 dark:text-white truncate">
                        {file.name || file.fileName}
                      </h5>
                      {file.createdAt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(file.createdAt)}
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm">
                      →
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Other Files */}
        {otherFiles.length > 0 && (
          <div>
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
              {language === 'fr' ? 'Autres fichiers' : 'Other Files'} ({otherFiles.length})
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {otherFiles.map((file, index) => (
                <Card
                  key={file.id || file.url || index}
                  variant="outlined"
                  hover
                  clickable
                  onClick={() => handleFileClick(file)}
                  className="flex flex-col items-center justify-center p-6 animate-fadeInUp"
                  style={{ animationDelay: `${(imageFiles.length + index) * 0.05}s` }}
                >
                  <div className="text-4xl mb-2">{getFileIcon(file)}</div>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center truncate w-full">
                    {file.name || file.fileName || `File ${index + 1}`}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Full Preview Modal */}
        {selectedFile && (
          <Modal
            isOpen={!!selectedFile}
            onClose={() => setSelectedFile(null)}
            title={
              <div className="flex items-center gap-2">
                <span className="text-xl">{getFileIcon(selectedFile)}</span>
                <span className="font-black text-gray-900 dark:text-white truncate">
                  {selectedFile.name || selectedFile.fileName}
                </span>
              </div>
            }
          >
            <div className="space-y-4">
              {isImage(selectedFile) ? (
                <div className="flex justify-center">
                  <img
                    src={selectedFile.url || selectedFile.path}
                    alt={selectedFile.name || selectedFile.fileName}
                    className="max-w-full max-h-96 object-contain rounded-xl"
                  />
                </div>
              ) : (
                <div className="text-center p-12">
                  <div className="text-6xl mb-4">{getFileIcon(selectedFile)}</div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {language === 'fr' ? 'Aperçu non disponible' : 'Preview not available'}
                  </p>
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    window.open(selectedFile.url || selectedFile.path, '_blank', 'noopener,noreferrer');
                  }}
                >
                  {language === 'fr' ? 'Ouvrir' : 'Open'} →
                </Button>
                <Button
                  variant="gradient"
                  fullWidth
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedFile.url || selectedFile.path;
                    link.download = selectedFile.name || selectedFile.fileName;
                    link.click();
                  }}
                >
                  {language === 'fr' ? 'Télécharger' : 'Download'} 💾
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Card>
  );
};

export default FileGallery;
